import { useCallback, useEffect, useRef, useState } from 'react';

export const SCROLL_CHUNK_PX = 200;

/**
 * Tracks the horizontal scroll state of an overflowing element:
 * whether it can scroll back/forward, and scrolling by a fixed chunk.
 */
export function useHorizontalScroll<T extends HTMLElement>(resetKey: unknown) {
  const scrollerRef = useRef<T>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const updateScrollIndication = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    /* abs() keeps the logic correct in RTL, where scrollLeft is negative */
    const scrolled = Math.abs(scroller.scrollLeft);
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    setCanScrollBack(scrolled > 1);
    setCanScrollForward(scrolled < maxScroll - 1);
  }, []);

  useEffect(() => {
    updateScrollIndication();
    const scroller = scrollerRef.current;
    scroller?.addEventListener('scroll', updateScrollIndication);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(updateScrollIndication);
    if (scroller) {
      resizeObserver?.observe(scroller);
    }
    return () => {
      scroller?.removeEventListener('scroll', updateScrollIndication);
      resizeObserver?.disconnect();
    };
  }, [resetKey, updateScrollIndication]);

  function scrollByChunk(direction: 1 | -1) {
    scrollerRef.current?.scrollBy?.({
      left: direction * SCROLL_CHUNK_PX,
      behavior: 'smooth',
    });
  }

  return { scrollerRef, canScrollBack, canScrollForward, scrollByChunk };
}
